// Photos of paper slips come straight off a phone camera, often 3-8 MB. They are shrunk in the browser before upload so they
// are fast on a mobile connection and well under the server's size limit; a PDF is sent as it is.

export const MAX_SLIP_FILES = 3;
export const MAX_SLIP_BYTES = 5 * 1024 * 1024;
const MAX_SIDE = 1600;
const QUALITY = 0.8;

const loadImage = (file) => new Promise((resolve, reject) => {
  const url = URL.createObjectURL(file);
  const img = new Image();
  img.onload = () => { URL.revokeObjectURL(url); resolve(img); };
  img.onerror = () => { URL.revokeObjectURL(url); reject(new Error('decode')); };
  img.src = url;
});

/**
 * Turns a file the user picked into one that is ready to upload: a PDF as is (if small enough), a photo shrunk to a JPEG.
 * Throws an Error with a message for the user when the file can't be used.
 */
export async function prepareSlipFile(file) {
  if (file.type === 'application/pdf') {
    if (file.size > MAX_SLIP_BYTES) throw new Error(`${file.name} is too large (5 MB at most)`);
    return file;
  }
  if (!/^image\//.test(file.type)) {
    throw new Error(`${file.name} is not a photo or PDF`);
  }
  let img;
  try {
    img = await loadImage(file);
  } catch (e) {
    // e.g. an iPhone HEIC photo on a browser that can't open it
    throw new Error(`${file.name} can't be opened here. Take the photo again, or choose a JPG or PNG.`);
  }
  const scale = Math.min(1, MAX_SIDE / Math.max(img.naturalWidth, img.naturalHeight));
  const canvas = document.createElement('canvas');
  canvas.width = Math.max(1, Math.round(img.naturalWidth * scale));
  canvas.height = Math.max(1, Math.round(img.naturalHeight * scale));
  const ctx = canvas.getContext('2d');
  ctx.fillStyle = '#ffffff'; // a transparent PNG would otherwise turn black in a JPEG
  ctx.fillRect(0, 0, canvas.width, canvas.height);
  ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
  const blob = await new Promise((resolve) => canvas.toBlob(resolve, 'image/jpeg', QUALITY));
  if (!blob) throw new Error(`${file.name} could not be prepared`);
  if (blob.size > MAX_SLIP_BYTES) throw new Error(`${file.name} is too large (5 MB at most)`);
  const stem = file.name.replace(/\.[^.]+$/, '') || 'slip';
  return new File([blob], `${stem}.jpg`, { type: 'image/jpeg' });
}

/** Uploads prepared files to a purchase, one after another. Returns how many failed (each failure is reported through onError). */
export async function uploadSlipFiles(axios, purchaseId, files, onError) {
  let failed = 0;
  for (const file of files) {
    const body = new FormData();
    body.append('file', file);
    try {
      await axios.post(`/api/purchase-invoices/${purchaseId}/attachments`, body);
    } catch (error) {
      failed += 1;
      const data = error.response?.data;
      onError(typeof data === 'string' && data ? data : `${file.name} could not be uploaded`);
    }
  }
  return failed;
}
